import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import AddClientModal from '../../components/Modals/AddClientModal/AddClientModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import axios from 'axios';
import { useAppContext } from '../../context/AppContext';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { muiMenuItemSx, sanitizeUrl } from '../../utils/functions';
import AuthenticatedImage from '../../components/AuthenticatedImage/AuthenticatedImage';
import { FaEdit, FaTrash, FaSortAlphaDown, FaSortAlphaUp, FaSort, FaBars, FaThLarge } from 'react-icons/fa';
import ClientCard from '../../components/ClientCard/ClientCard';
import { useNotification } from '../../components/notifications/useNotification';
import TourButton from '../../components/TourButton/TourButton';
import { getClientsPageSteps } from '../../tours/clientsPage';
import './Clients.css';


const Clients = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { dietist } = useAppContext();
    const { showError } = useNotification();

    const tourSteps = useMemo(() => getClientsPageSteps(t), [t]);

    /* ── Data ─────────────────────────────────────────────────────────── */
    const [clientsList,  setClientsList]  = useState([]);
    const [currentPage,  setCurrentPage]  = useState(1);
    const [totalPages,   setTotalPages]   = useState(1);
    const [totalCount,   setTotalCount]   = useState(0);

    /* ── Filters ──────────────────────────────────────────────────────── */
    const [searchFilter, setSearchFilter] = useState('');
    const [sortFilter,   setSortFilter]   = useState(''); // '' | 'name1' | 'name-1'
    const [statusFilter, setStatusFilter] = useState(''); // '' | 'active' | 'inactive'
    const limitOptions = [10, 25, 50, 100];
    const [limit, setLimit] = useState(10);
    const [displayFilter, setDisplayFilter] = useState('list'); // 'list' | 'grid'

    /* ── Modals ───────────────────────────────────────────────────────── */
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [editClient,         setEditClient]         = useState(null);
    const [deleteClient,       setDeleteClient]       = useState(null);

    /* ── Init ─────────────────────────────────────────────────────────── */
    useEffect(() => {
        getData(1);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── API ──────────────────────────────────────────────────────────── */
    const getData = async (
        page,
        sort   = sortFilter,
        search = searchFilter,
        status = statusFilter,
        lim    = limit,
    ) => {
        try {
            const params = { page, limit: lim };
            if (sort)   params.sortFilter   = sort;
            if (search) params.searchFilter = search;
            if (status) params.statusFilter = status;
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/clients`,
                { params, withCredentials: true }
            );
            setClientsList(data.clients);
            setTotalPages(data.totalPages);
            // Use totalCount from API when present; fall back to a rough estimate
            setTotalCount(data.totalCount ?? data.totalPages * lim);
        } catch (e) {
            console.error(e);
            showError(t('unknown-error'));
        }
    };

    /* ── Handlers ─────────────────────────────────────────────────────── */

    /** Cycle: '' → 'name1' (A→Z) → 'name-1' (Z→A) → '' */
    const handleNameSort = async () => {
        const next = sortFilter === '' ? 'name1' : sortFilter === 'name1' ? 'name-1' : '';
        setSortFilter(next);
        setCurrentPage(1);
        await getData(1, next, searchFilter, statusFilter, limit);
    };

    /** Cycle: '' (all) → 'active' → 'inactive' → '' */
    const handleStatusFilter = async () => {
        const next = statusFilter === '' ? 'active' : statusFilter === 'active' ? 'inactive' : '';
        setStatusFilter(next);
        setCurrentPage(1);
        await getData(1, sortFilter, searchFilter, next, limit);
    };

    const handleSearch = async (e) => {
        const v = e.target.value;
        setSearchFilter(v);
        await getData(currentPage, sortFilter, v, statusFilter, limit);
    };

    const handlePageChange = async (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        await getData(page);
    };

    const handleLimitChange = async (e) => {
        const newLimit = parseInt(e.target.value);
        setLimit(newLimit);
        setCurrentPage(1);
        await getData(1, sortFilter, searchFilter, statusFilter, newLimit);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/clients/${deleteClient._id}`,
                { withCredentials: true }
            );
            setDeleteClient(null);
            await getData(currentPage);
        } catch (e) {
            console.error(e);
            showError(t('unknown-error'));
        }
    };

    /* ── Derived values ───────────────────────────────────────────────── */
    const showingFrom = clientsList.length === 0 ? 0 : (currentPage - 1) * limit + 1;
    const showingTo   = (currentPage - 1) * limit + clientsList.length;

    /** Page-number window: current page ± 2, clamped to [1, totalPages]. */
    const pageWindow = () => {
        const pages = [];
        for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
            pages.push(i);
        }
        return pages;
    };

    /* ── Sub-components ───────────────────────────────────────────────── */
    const NameSortIcon = () => {
        if (sortFilter === 'name1')  return <FaSortAlphaDown className="cl-sort-icon cl-sort-icon--active" />;
        if (sortFilter === 'name-1') return <FaSortAlphaUp   className="cl-sort-icon cl-sort-icon--active" />;
        return <FaSort className="cl-sort-icon" />;
    };

    /* MUI variant="standard" select for the rows-per-page control */
    const muiLimitSx = {
        '&:before': { borderBottomColor: 'var(--tertiary-color)' },
        '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'var(--secondary-color)' },
        '&:after': { borderBottomColor: 'var(--primary-color)' },
        '& .MuiSelect-select': { padding: '2px 24px 2px 4px', fontSize: '0.8rem', fontWeight: 600 },
        '& .MuiSelect-icon': { color: 'var(--secondary-color)', right: 0 },
    };

    /* ── Render ───────────────────────────────────────────────────────── */
    return (
        <div id="clients" className="cl-page">

            {/* ── Header: search + add button ── */}
            <div className="cl-header" data-tour="cl-header">
                <SearchInput
                    name="clients-search-input"
                    value={searchFilter}
                    placeholder={t('search-by-name')}
                    onChange={handleSearch}
                />
                <Button
                    label={t('new-client')}
                    onClick={() => setShowAddClientModal(true)}
                />
                <TourButton steps={tourSteps} label={t('tour.tutorial-btn')} />
            </div>

            {/* ── Filters / view-toggle row ── */}
            <div className="cl-filters-row" data-tour="cl-filters">
                {/* Name sort */}
                <button
                    className={`cl-filter-btn${sortFilter ? ' cl-filter-btn--active' : ''}`}
                    onClick={handleNameSort}
                    title={t('Sort')}
                >
                    {t('Name')} <NameSortIcon />
                </button>

                {/* Status filter */}
                <button
                    className={`cl-filter-btn${statusFilter ? ' cl-filter-btn--active' : ''}`}
                    onClick={handleStatusFilter}
                    title={t('diet-status')}
                >
                    {statusFilter === 'active'
                        ? t('diet-active')
                        : statusFilter === 'inactive'
                            ? t('diet-inactive')
                            : t('diet-status')}
                    <span className={`cl-status-dot${
                        statusFilter === 'active'   ? ' cl-status-dot--active'   :
                        statusFilter === 'inactive' ? ' cl-status-dot--inactive' : ''
                    }`} />
                </button>

                {/* View toggle — pushed to the right */}
                <div className="cl-view-toggle">
                    {/* limit selector only if in grid view */}
                    {displayFilter === 'grid' && (
                        <FormControl variant="standard" size="small">
                            <MuiSelect
                                variant="standard"
                                value={limit.toString()}
                                onChange={handleLimitChange}
                                sx={muiLimitSx}
                            >
                                {limitOptions.map(n => (
                                    <MenuItem key={n} value={n.toString()} sx={muiMenuItemSx}>{n}</MenuItem>
                                ))}
                            </MuiSelect>
                        </FormControl>
                    )}
                    <button
                        className={`cl-view-btn${displayFilter === 'list' ? ' cl-view-btn--active' : ''}`}
                        onClick={() => setDisplayFilter('list')}
                        title="List view"
                        aria-label="List view"
                    >
                        <FaBars />
                    </button>
                    <button
                        className={`cl-view-btn${displayFilter === 'grid' ? ' cl-view-btn--active' : ''}`}
                        onClick={() => setDisplayFilter('grid')}
                        title="Grid view"
                        aria-label="Grid view"
                    >
                        <FaThLarge />
                    </button>
                </div>
            </div>

            {/* ── Grid view ── */}
            {displayFilter === 'grid' && (
                <div className="cl-cards-grid" data-tour="cl-content">
                    {clientsList.length === 0 ? (
                        <p className="cl-cards-empty">{t('no-clients-found')}</p>
                    ) : clientsList.map((client) => (
                        <ClientCard
                            key={client._id}
                            client={client}
                            updateClientsPage={getData}
                            currentPage={currentPage}
                        />
                    ))}
                </div>
            )}
            {/* ── List view (table card) ── */}
            {displayFilter === 'list' && (
            <div className="cl-table-wrap" data-tour="cl-content">
                <table className="cl-table">
                    <thead>
                        <tr className="cl-thead-row">
                            {/* Avatar column — no label */}
                            <th className="cl-th cl-th-avatar" aria-label="avatar" />

                            {/* Name — sortable on click (cycles: default → A-Z → Z-A) */}
                            <th className="cl-th cl-th-name">
                                <button className="cl-th-sort-btn" onClick={handleNameSort} title={t('Sort')}>
                                    {t('Name')} <NameSortIcon />
                                </button>
                            </th>

                            {/* Email */}
                            <th className="cl-th cl-th-email">{t('Email')}</th>

                            {/* Phone */}
                            <th className="cl-th cl-th-phone">{t('Phone')}</th>

                            {/* Diet status — filterable on click (cycles: all → active → inactive) */}
                            <th className="cl-th cl-th-status">
                                <button
                                    className={`cl-th-sort-btn${statusFilter ? ' cl-th-sort-btn--filtered' : ''}`}
                                    onClick={handleStatusFilter}
                                    title={t('diet-status')}
                                >
                                    {statusFilter === 'active'
                                        ? t('diet-active')
                                        : statusFilter === 'inactive'
                                            ? t('diet-inactive')
                                            : t('diet-status')}
                                    <span className={`cl-status-dot${
                                        statusFilter === 'active'   ? ' cl-status-dot--active'   :
                                        statusFilter === 'inactive' ? ' cl-status-dot--inactive' : ''
                                    }`} />
                                </button>
                            </th>

                            {/* Actions */}
                            <th className="cl-th cl-th-actions">{t('Actions')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {clientsList.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="cl-td-empty">
                                    {t('no-clients-found')}
                                </td>
                            </tr>
                        ) : clientsList.map((client) => (
                            <tr
                                key={client._id}
                                className="cl-tbody-row"
                                onClick={() => navigate(`/client/${client._id}`)}
                            >
                                {/* Profile photo */}
                                <td className="cl-td cl-td-avatar">
                                    <AuthenticatedImage
                                        src={sanitizeUrl(client.profilePhotoUrl)}
                                        fallbackSrc='/images/anonymous_avatar_icon.jpg'
                                        alt=""
                                        className="cl-avatar"
                                    />
                                </td>

                                {/* Name + surname */}
                                <td className="cl-td cl-td-name">
                                    <span className="cl-name">{client.name} {client.surname}</span>
                                </td>

                                {/* Email */}
                                <td className="cl-td cl-td-email">{client.email || '—'}</td>

                                {/* Phone */}
                                <td className="cl-td cl-td-phone">{client.phone || '—'}</td>

                                {/* Diet status badge */}
                                <td className="cl-td cl-td-status">
                                    <span className={`cl-badge${client.dietStatus ? ' cl-badge--active' : ' cl-badge--inactive'}`}>
                                        {client.dietStatus ? t('diet-active') : t('diet-inactive')}
                                    </span>
                                </td>

                                {/* Edit / delete — stopPropagation so row click doesn't navigate */}
                                <td
                                    className="cl-td cl-td-actions"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        className="cl-action-btn cl-action-btn--edit"
                                        title={t('edit-client')}
                                        onClick={() => setEditClient(client)}
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        className="cl-action-btn cl-action-btn--delete"
                                        title={t('delete-client')}
                                        onClick={() => setDeleteClient(client)}
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ── Footer: showing info + rows-per-page + pagination ── */}
                <div className="cl-footer" data-tour="cl-footer">
                    <span className="cl-showing">
                        {t('table-showing', { from: showingFrom, to: showingTo, total: totalCount })}
                    </span>

                    <div className="cl-footer-right">
                        {/* Rows-per-page selector */}
                        <FormControl variant="standard" size="small">
                            <MuiSelect
                                variant="standard"
                                value={limit.toString()}
                                onChange={handleLimitChange}
                                sx={muiLimitSx}
                            >
                                {limitOptions.map(n => (
                                    <MenuItem key={n} value={n.toString()} sx={muiMenuItemSx}>{n}</MenuItem>
                                ))}
                            </MuiSelect>
                        </FormControl>

                        {/* Page buttons */}
                        <div className="cl-pagination">
                            <button
                                className="cl-pag-btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                                aria-label="first page"
                            >«</button>
                            <button
                                className="cl-pag-btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                                aria-label="previous page"
                            >‹</button>
                            {pageWindow().map(p => (
                                <button
                                    key={p}
                                    className={`cl-pag-btn${p === currentPage ? ' cl-pag-btn--active' : ''}`}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="cl-pag-btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                                aria-label="next page"
                            >›</button>
                            <button
                                className="cl-pag-btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                                aria-label="last page"
                            >»</button>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* ── Modals ── */}
            {showAddClientModal && (
                <AddClientModal
                    setShowModal={setShowAddClientModal}
                    onSave={() => getData(currentPage)}
                />
            )}
            {editClient && (
                <AddClientModal
                    client={editClient}
                    setShowModal={() => setEditClient(null)}
                    onSave={() => { setEditClient(null); getData(currentPage); }}
                />
            )}
            {deleteClient && (
                <ConfirmationModal
                    titleText={`${t('delete-client')}: ${deleteClient.name} ${deleteClient.surname}`}
                    bodyText={t('confirm-delete-client')}
                    handleConfirm={handleDeleteConfirm}
                    handleClose={() => setDeleteClient(null)}
                />
            )}
        </div>
    );
};

export default Clients;
